import {
  faMinusCircle,
  faPlusCircle,
  faQuestionCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FieldArray, Formik, FormikProps, useFormikContext } from 'formik';
import { FC, useEffect, useRef } from 'react';
import {
  DirectFcfsNotificationSettingsItem,
  DirectFcfsNotificationSettingsType,
} from 'src/apis/direct-fcfs/types';
import { Button } from 'src/components/buttons-and-actions/button';
import { Card } from 'src/components/data-display/card';
import { Checkbox } from 'src/components/form/checkbox';
import { FormContainer } from 'src/components/form/form-container';
import { Input } from 'src/components/form/input';
import { Stack } from 'src/components/layout/stack';
import { Tooltip } from 'src/components/overlay/tooltip';
import { PageSpinner } from 'src/components/spinner-container';
import { Heading } from 'src/components/text/heading';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useToast } from 'src/hooks/use-toasts';
import {
  useNotificationSettings,
  useOrganizationApiNotificationConfig,
} from 'src/pages/settings/direct-fcfs/use-notification-settings';
import {
  useNotificationSettingsUpdate,
  useOrganizationApiNotificationConfigUpdate,
} from 'src/pages/settings/direct-fcfs/use-notification-settings-update';
import { array, boolean, InferType, number, object, string } from 'yup';

export const notificationSettingsTypeLabels: Record<
  DirectFcfsNotificationSettingsType,
  string
> = {
  SHIPPER_COMMUNICATION: 'Shipper Communication',
  TSO_COMMUNICATION: 'TSO Communication',
};

export const notificationSettingsTypeTooltips: Record<
  DirectFcfsNotificationSettingsType,
  string
> = {
  SHIPPER_COMMUNICATION: 'Confirmation or rejection emails to shipper',
  TSO_COMMUNICATION:
    'Confirmation (+Excel file) or unsuccessful participation to TSO',
};

const cleanupEmailList = (list: InferType<typeof emailSchema>[]) =>
  list.filter((i) => !!i) as string[];

const emailSchema = string().email(() => () => 'Invalid email address');

const settingsItemSchema = object({
  type: string<DirectFcfsNotificationSettingsType>().required(),
  emailTo: array().of(emailSchema).defined(),
  emailCc: array().of(emailSchema).defined(),
})
  .defined()
  .test('atLeastOneToEmailIsRequired', '', (item, { createError, path }) => {
    const toEmails = cleanupEmailList(item?.emailTo ?? []);
    if (item?.type === 'SHIPPER_COMMUNICATION' || toEmails.length > 0)
      return true;
    return createError({
      path: `${path}.emailTo[0]`,
      message: (() => () =>
        'Please add at least one To email address') as unknown as string,
    });
  });

const settingsSchema = array().of(settingsItemSchema).defined();

type SettingsFormType = InferType<typeof settingsSchema>;

const apiSettingsSchema = object({
  enabled: boolean().required(),
  endpoint: string()
    .url(() => () => 'Endpoint must be a valid URL.')
    .required(),
  secondsBetweenAttempts: number()
    .min(0.1)
    .max(
      600,
      () => () => 'Interval between attempts has to be less than or equal 600.'
    )
    .required(),
  maxAttempts: number()
    .min(1)
    .max(10, () => () => 'Maximum attempts has to be less than or equal 10.')
    .required(),
  authorization: object()
    .shape({
      headerName: string().required(),
      headerValue: string().required(),
    })
    .required(),
  fallbackEmails: array().of(emailSchema).defined(),
}).defined();

type ApiSettingsFormType = InferType<typeof apiSettingsSchema>;

export const Form: FC = () => {
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const notification = useToast();
  const settingsRequest = useNotificationSettings();
  const settingsUpdateRequest = useNotificationSettingsUpdate();

  const apiSettingsRequest = useOrganizationApiNotificationConfig();
  const apiSettingsUpdateRequest = useOrganizationApiNotificationConfigUpdate();

  const formRef =
    useRef<FormikProps<DirectFcfsNotificationSettingsItem[]>>(null);
  const formRefApi = useRef<FormikProps<ApiSettingsFormType>>(null);

  useEffect(() => {
    if (!settingsUpdateRequest.response) return;
    notification({ type: 'success', children: 'Saved' });
    settingsRequest.execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsUpdateRequest.response]);

  useEffect(() => {
    if (!settingsRequest.response || !formRef.current) return;
    const settings = settingsRequest.response.data;
    const values = settings.map(({ type, emailTo, emailCc }) => {
      if (type === 'SHIPPER_COMMUNICATION') {
        emailTo = ['', ...emailTo];
      }
      if (emailTo.length === 0) {
        emailTo = [''];
      }
      if (emailCc.length === 0) {
        emailCc = [''];
      }
      return { type, emailTo, emailCc };
    });

    formRef.current.resetForm({ values });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsRequest.response]);

  useEffect(() => {
    if (!apiSettingsUpdateRequest.response) return;
    notification({ type: 'success', children: 'Saved' });
    apiSettingsRequest.execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiSettingsUpdateRequest.response]);

  useEffect(() => {
    if (!apiSettingsRequest.response || !formRefApi.current) return;
    const apiSettings = apiSettingsRequest.response.data;
    const values = {
      ...apiSettings,
      secondsBetweenAttempts:
        apiSettings.intervalBetweenAttemptsInMillis / 1000,
      fallbackEmails: apiSettings.fallbackEmails ?? [],
    };
    formRefApi.current.resetForm({ values });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiSettingsRequest.response]);

  // Add spinner
  if (!settingsRequest.response && !apiSettingsRequest.response)
    return <PageSpinner />;

  const onSubmit = (data: SettingsFormType) => {
    settingsUpdateRequest.execute(
      data.map((item) => ({
        type: item.type,
        emailTo: cleanupEmailList(item.emailTo),
        emailCc: cleanupEmailList(item.emailCc),
      }))
    );
  };

  const onSubmitApiSettings = (data: ApiSettingsFormType) => {
    const payload = {
      ...data,
      fallbackEmails: cleanupEmailList(data.fallbackEmails),
      intervalBetweenAttemptsInMillis: data.secondsBetweenAttempts * 1000,
      secondsBetweenAttempts: undefined,
    };
    apiSettingsUpdateRequest.execute(payload);
  };

  return (
    <>
      <Card>
        <Formik<DirectFcfsNotificationSettingsItem[]>
          innerRef={formRef}
          initialValues={[]}
          onSubmit={onSubmit}
          validationSchema={settingsSchema}
        >
          {({ values }) => (
            <FormContainer mode="regular">
              {values.map((_, idx) => (
                <ItemForm key={idx} index={idx} />
              ))}

              <Stack flow="column" justifyContent="space-between">
                <Button
                  mode="secondary"
                  data-testid="reset"
                  onClick={() => settingsRequest.execute()}
                >
                  Reset
                </Button>
                <Button type="submit" data-testid="save">
                  Save
                </Button>
              </Stack>
            </FormContainer>
          )}
        </Formik>
      </Card>

      {monolithUser?.hasTsoAdminRole && (
        <>
          <Heading mode="section">API Settings</Heading>
          <Card>
            <Formik<ApiSettingsFormType>
              innerRef={formRefApi}
              initialValues={
                {
                  enabled: false,
                  fallbackEmails: [],
                } as unknown as ApiSettingsFormType
              }
              onSubmit={onSubmitApiSettings}
              validationSchema={apiSettingsSchema}
            >
              {() => (
                <FormContainer mode="regular">
                  <ApiSettingsForm />

                  <Stack flow="column" justifyContent="space-between">
                    <Button
                      mode="secondary"
                      data-testid="notification-settings-reset"
                      onClick={() => apiSettingsRequest.execute()}
                    >
                      Reset
                    </Button>
                    <Button
                      type="submit"
                      data-testid="notification-settings-save"
                    >
                      Save
                    </Button>
                  </Stack>
                </FormContainer>
              )}
            </Formik>
          </Card>
        </>
      )}
    </>
  );
};

const ItemForm: FC<{
  index: number;
}> = ({ index }) => {
  const { values } = useFormikContext<DirectFcfsNotificationSettingsItem[]>();
  const type = values[index].type;
  return (
    <Card>
      <Stack gap={2}>
        <Heading mode="card">
          {notificationSettingsTypeLabels[type]}{' '}
          <Tooltip
            content={
              <Stack gap={1}>
                <p>{notificationSettingsTypeTooltips[type]}</p>
              </Stack>
            }
          >
            {(targetProps) => (
              <FontAwesomeIcon icon={faQuestionCircle} aria-hidden />
            )}
          </Tooltip>
        </Heading>
        <div>
          <EmailArrayForm
            index={index}
            field="emailTo"
            required
            label="To"
            firstDisabled={type === 'SHIPPER_COMMUNICATION'}
          />
        </div>
        <EmailArrayForm index={index} field="emailCc" label="CC" />
      </Stack>
    </Card>
  );
};

const EmailArrayForm: FC<{
  index: number;
  field: 'emailTo' | 'emailCc';
  label: string;
  required?: boolean;
  firstDisabled?: boolean;
}> = ({ index, field, label, required = false, firstDisabled = false }) => {
  const { values } = useFormikContext<SettingsFormType>();
  const fieldBaseName = `[${index}].${field}`;
  const value = values[index][field] as string[];
  return (
    <EmailArrayFormComponent
      fieldBaseName={fieldBaseName}
      value={value}
      label={label}
      required={required}
      firstDisabled={firstDisabled}
    />
  );
};

const EmailArrayFormComponent: FC<{
  fieldBaseName: string;
  value: string[];
  label: string;
  required?: boolean;
  firstDisabled?: boolean;
}> = ({
  fieldBaseName,
  value,
  label,
  required = false,
  firstDisabled = false,
}) => {
  if (value.length === 0) value = [''];
  return (
    <FieldArray
      name={fieldBaseName}
      render={({ push, remove }) => (
        <Stack templateColumns="auto" gap={1}>
          {value.map((_, idx) => {
            const first = idx === 0;
            const disabled = first && firstDisabled;
            return (
              <Stack
                flow="column"
                key={idx}
                templateColumns="1fr auto"
                alignItems="center"
                gap={0}
              >
                <Input
                  type="text"
                  name={`${fieldBaseName}[${idx}]`}
                  label={
                    first ? `${label}${required && !disabled ? '*' : ''}` : ''
                  }
                  disabled={disabled}
                  placeholder={disabled ? 'the shipper user' : undefined}
                  addon={
                    <Button
                      mode="icon"
                      onClick={() => remove(idx)}
                      disabled={idx === 0}
                    >
                      <FontAwesomeIcon icon={faMinusCircle} aria-hidden />{' '}
                      Remove
                    </Button>
                  }
                />
              </Stack>
            );
          })}
          <Button
            mode="icon"
            onClick={() => push('')}
            style={{ justifySelf: 'end' }}
          >
            <FontAwesomeIcon icon={faPlusCircle} aria-hidden /> Add Recipient
          </Button>
        </Stack>
      )}
    />
  );
};

const ApiSettingsForm: FC = () => {
  const { values } = useFormikContext<ApiSettingsFormType>();
  const fallbackEmails: string[] = (values.fallbackEmails as string[]) ?? [];
  return (
    <Card>
      <Stack gap={2}>
        <Checkbox
          name="enabled"
          label="Enabled"
          optionLabel=""
          data-testid="notification-settings-check"
        />
        <Input
          name="endpoint"
          label="Endpoint"
          placeholder="Endpoint URL"
          data-testid="notification-settings-endpoint"
          markAsRequired
        />
        <Input
          type="number"
          name="secondsBetweenAttempts"
          label="Interval between attempts"
          placeholder="E.g. 10"
          unit="seconds"
          data-testid="notification-settings-interval"
          markAsRequired
        />
        <Input
          type="number"
          name="maxAttempts"
          label="Maximum attempts"
          placeholder="E.g. 2"
          unit="times"
          data-testid="notification-settings-max-attempts"
          markAsRequired
        />
        <Input
          name="authorization.headerName"
          label="Header name"
          placeholder="Header name"
          data-testid="notification-settings-header-name"
          markAsRequired
        />
        <Input
          name="authorization.headerValue"
          type="password"
          autoComplete="new-password"
          label="Header value"
          placeholder="Header value"
          data-testid="notification-settings-header-value"
          markAsRequired
        />
        <EmailArrayFormComponent
          fieldBaseName="fallbackEmails"
          value={fallbackEmails}
          label="Fallback Email To"
        />
      </Stack>
    </Card>
  );
};
